import { cn } from "../../lib/utils";
import { UserSearchableSelect } from "../user-searchable-select";
import {
  AmountCell,
  ButtonCell,
  ConclusionCell,
  DateCell,
  DropdownCell,
  LinkCell,
  NumericCell,
  ProgressCell,
  SelectCell,
  TagCell,
  TextCell,
  UserCell,
} from "./cells";
import { StatusCell } from "./cells/status-cell";
import type {
  AmountCellProps,
  ButtonCellProps,
  ConclusionCellProps,
  LinkCellProps,
  StatusCellProps,
  TableUserCellProps,
  TagCellProps,
} from "./types";

type CellPropsFunction<T, P = unknown> = (row: T) => P;
type CellPropsType<T, P = unknown> = P | CellPropsFunction<T, P>;

type CellRendererColumn<T, P = unknown> =
  | {
      type: string;
      cellProps?: CellPropsType<T, P>;
      accessorKey: keyof T | "actions" | "status";
      accessorFn?: never;
    }
  | {
      type: string;
      cellProps?: CellPropsType<T, P>;
      accessorKey?: never;
      accessorFn: (row: T) => string | number | boolean;
    };

export interface CellRendererProps<T> {
  column: CellRendererColumn<T>;
  value:
    | string
    | number
    | Date
    | AmountCellProps["value"]
    | TagCellProps["value"]
    | ConclusionCellProps["value"]
    | ButtonCellProps["buttons"]
    | TableUserCellProps["value"]
    | unknown;
  row: T;
}

export function CellRenderer<T>({ column, value, row }: CellRendererProps<T>) {
  const cellProps =
    typeof column.cellProps === "function"
      ? column.cellProps(row)
      : (column.cellProps ?? {});

  switch (column.type) {
    case "text":
      return <TextCell value={value as string} className="cursor-text" />;

    case "numeric":
      return <NumericCell value={value as number} className="cursor-text" />;

    case "date":
      return (
        <DateCell
          value={value as string | Date}
          className="cursor-text"
          locale={cellProps.locale}
        />
      );

    case "amount":
      return (
        <AmountCell
          currency={cellProps.currency}
          value={value as number}
          className="cursor-text"
        />
      );

    case "tag":
      return <TagCell value={value as TagCellProps["value"]} {...cellProps} />;

    case "badge":
      return <ConclusionCell value={String(value)} {...cellProps} />;
    case "progress":
      return <ProgressCell value={value as number} {...cellProps} />;

    case "select":
      return (
        <SelectCell
          value={value as string | number}
          options={cellProps.options || []}
          onChange={(newValue: string | number) => {
            if (cellProps.onChange) {
              cellProps.onChange(newValue, row);
            }
          }}
          className={cn("w-full", cellProps.className)}
          disabled={cellProps.disabled}
        />
      );

    case "button":
      return (
        <ButtonCell
          buttons={value as ButtonCellProps["buttons"]}
          {...cellProps}
        />
      );

    case "dropdown":
      return (
        <DropdownCell
          value={value as string}
          options={cellProps.options || []}
          onChange={(newValue: string) => {
            if (cellProps.onChange) {
              cellProps.onChange(newValue, row);
            }
          }}
          className={cn("w-full", cellProps.className)}
          disabled={cellProps.disabled}
          placeholder={cellProps.placeholder}
        />
      );

    case "user":
      return (
        <UserCell
          value={value as TableUserCellProps["value"]}
          className="mx-auto"
          {...cellProps}
        />
      );
    case "status":
      return (
        <StatusCell
          value={value as StatusCellProps["value"]}
          label={cellProps.label}
          {...cellProps}
        />
      );

    case "ownership-selector":
      return (
        <div className="max-w-[280px] pb-1">
          <UserSearchableSelect
            users={cellProps.users}
            value={cellProps.value ? [cellProps.value] : ["unassigned"]}
            defaultValue={cellProps.defaultValue ?? undefined}
            isLoading={cellProps.isLoading}
            error={cellProps.error}
            disabled={cellProps.disabled}
            placeholder={cellProps.placeholder}
            onSelect={cellProps.onSelect}
            onOpenChange={cellProps.onOpenChange}
            className="border-none group-hover:bg-blue-50"
          />
        </div>
      );

    case "link":
      return (
        <LinkCell
          value={value as LinkCellProps["value"]}
          to={cellProps.to || "#"}
          maxLines={cellProps.maxLines}
          className={cellProps.className}
          showTooltip={cellProps.showTooltip}
        />
      );
    default:
      return value as string;
  }
}
